import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, ArrowLeft, Sparkles, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import Reveal from "../components/site/Reveal";
import LivingSectionTitle from "../components/site/LivingSectionTitle";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import VisualAnchor from "../components/site/ui/VisualAnchor";
import { useConsultation } from "../context/ConsultationContext";

// Maps Clarity Snapshot archetypes → Consultation modal "primary path" category.
// Archetypes that don't have an obvious mapping fall back to "Not sure yet".
// Keys are the lowercase archetype IDs the backend returns in result.archetype.
const ARCHETYPE_TO_CATEGORY = {
  clarifier: "Clarify",
  restorer: "Clarify",
  builder: "Build",
  messenger: "Express",
  alchemist: "Scale",
  integrator: "Scale",
};

/**
 * FrequencySnapshotPage — 7-question reflection quiz.
 *
 * Flow:
 *   intro → questions (one at a time) → optional email capture → result
 *
 * Design notes:
 * - Single-column premium layout. Progress dots at the top. Each
 *   question slides in via LivingSectionTitle's "flow" variant so the
 *   UX matches the rest of the site.
 * - The email capture is OPTIONAL and shown *after* answering all
 *   questions but *before* the result reveal. The user can skip it.
 *   This is the recommended pattern: don't gate insight behind email,
 *   but capture it at peak intent (right before the reflection lands).
 * - Result + recommended_next are persisted to localStorage so the
 *   Smart-CTA system on the homepage can adapt copy after the visitor
 *   returns.
 */

const RECOMMENDED_LABELS = {
  blueprint: {
    label: "Turn this Snapshot into your Personal Action Plan",
    href: "/?checkout=blueprint#blueprint",
    cta: "Start the Personal Action Plan",
  },
  consultation: {
    label: "Name the frame in a focused 30-minute session",
    href: "/orientation-reset/schedule",
    cta: "Book the Clarity Reset Session · $149",
  },
  book: {
    label: "Start with the foundational text",
    href: "/book",
    cta: "Get the Book",
  },
};

const FrequencySnapshotPage = () => {
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState("intro"); // intro | quiz | email | result
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/frequency-snapshot/questions")
      .then((r) => setQuestions(r.data.questions || []))
      .catch(() => setError("Could not load the snapshot. Please refresh."));
  }, []);

  const allAnswered = useMemo(
    () => questions.length > 0 && questions.every((q) => answers[q.id]),
    [questions, answers]
  );

  const answer = useCallback((qid, cid) => {
    setAnswers((prev) => ({ ...prev, [qid]: cid }));
    // Auto-advance after a brief beat so the tap feels acknowledged.
    setTimeout(() => {
      setQIndex((i) => {
        if (i < questions.length - 1) return i + 1;
        return i;
      });
    }, 220);
  }, [questions.length]);

  useEffect(() => {
    if (allAnswered && step === "quiz") {
      // Brief delay so the last answer's animation lands first.
      const t = setTimeout(() => setStep("email"), 320);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [allAnswered, step]);

  const submit = useCallback(async ({ withEmail }) => {
    try {
      setSubmitting(true);
      setError("");
      const r = await api.post("/frequency-snapshot/submit", {
        answers,
        email: withEmail ? email || null : null,
        name: withEmail ? name || null : null,
      });
      const res = r.data.result;
      setResult(res);
      try {
        localStorage.setItem("am_completed_snapshot", "1");
        localStorage.setItem("am_snapshot_result", JSON.stringify({
          archetype: res.archetype,
          recommended_next: res.recommended_next,
          completed_at: new Date().toISOString(),
        }));
      } catch { /* localStorage may be disabled — non-fatal */ }
      setStep("result");
    } catch {
      setError("Something interrupted the signal. Please try once more.");
    } finally {
      setSubmitting(false);
    }
  }, [answers, email, name]);

  const reset = () => {
    setStep("intro");
    setQIndex(0);
    setAnswers({});
    setEmail("");
    setName("");
    setResult(null);
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-themed noise-overlay pt-28" data-testid="snapshot-page">
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-12 md:py-20">
          {step === "intro" && (
            <Intro onBegin={() => setStep("quiz")} />
          )}

          {step === "quiz" && questions.length > 0 && (
            <Quiz
              questions={questions}
              qIndex={qIndex}
              setQIndex={setQIndex}
              answers={answers}
              onAnswer={answer}
            />
          )}

          {step === "email" && (
            <EmailGate
              email={email}
              setEmail={setEmail}
              name={name}
              setName={setName}
              submitting={submitting}
              onSubmitWithEmail={() => submit({ withEmail: true })}
              onSubmitSkip={() => submit({ withEmail: false })}
              error={error}
            />
          )}

          {step === "result" && result && (
            <Result result={result} onRetake={reset} />
          )}

          {error && step !== "email" && (
            <p className="text-rose-400 text-sm mt-6 text-center">{error}</p>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

// ── Intro ───────────────────────────────────────────────────────────
const Intro = ({ onBegin }) => (
  <>
    <LivingSectionTitle
      eyebrow="The Clarity Snapshot"
      parts={[
        { text: "Find your" },
        { text: "current signal.", italic: true },
      ]}
      align="center"
      variant="signal"
      className="text-4xl sm:text-5xl text-themed"
    />
    <Reveal delay={120}>
      <p className="mt-8 text-themed-muted text-center text-base md:text-lg leading-relaxed">
        The Clarity Snapshot is a free reflective diagnostic that helps you see where your momentum is strong, where it is blocked, and what kind of next step may make sense. Seven short questions. About two minutes.
      </p>
    </Reveal>
    <Reveal delay={220}>
      <ul className="mt-10 max-w-md mx-auto text-themed-muted text-sm space-y-2.5">
        <li className="flex items-start gap-3">
          <Sparkles className="text-gold mt-0.5 shrink-0" size={14} strokeWidth={1.4} />
          This is a reflection to help you choose a next step, not a diagnosis or a test.
        </li>
        <li className="flex items-start gap-3">
          <Sparkles className="text-gold mt-0.5 shrink-0" size={14} strokeWidth={1.4} />
          Your email is optional. You can see your result without sharing it.
        </li>
        <li className="flex items-start gap-3">
          <Sparkles className="text-gold mt-0.5 shrink-0" size={14} strokeWidth={1.4} />
          If you do share it, you also receive the Morning Frequency Set.
        </li>
      </ul>
    </Reveal>
    <Reveal delay={260}>
      <div className="mt-10 mx-auto max-w-xs" data-testid="snapshot-anchor-signal">
        <VisualAnchor
          src="/assets/brand/signal.webp"
          alt="A figure reading the gold lines of the signal. Finding the Signal."
          aspect="aspect-[928/1152]"
          className="rounded-md border border-gold/15 bg-black/30"
          imgClassName="object-contain"
        />
        <p className="mt-3 text-center text-[10px] tracking-[0.4em] uppercase text-gold/80">
          Finding the Signal
        </p>
      </div>
    </Reveal>
    <Reveal delay={340}>
      <div className="mt-12 text-center">
        <button
          onClick={onBegin}
          className="btn-gold inline-flex items-center gap-2"
          data-testid="snapshot-begin"
        >
          Begin the Snapshot <ArrowRight size={14} />
        </button>
      </div>
    </Reveal>
  </>
);

// ── Quiz ─────────────────────────────────────────────────────────────
const Quiz = ({ questions, qIndex, setQIndex, answers, onAnswer }) => {
  const q = questions[qIndex];
  const selected = answers[q.id];

  return (
    <div data-testid="snapshot-quiz">
      <ProgressDots total={questions.length} current={qIndex} answers={answers} questions={questions} />
      <div className="mt-12">
        {qIndex === 0 && (
          <p
            data-testid="snapshot-mirror-line"
            className="mb-7 text-center text-themed-muted text-sm md:text-[15px] font-serif italic max-w-md mx-auto leading-relaxed"
          >
            This is not a test. It is a mirror. Answer based on what feels most true right now.
          </p>
        )}
        <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4 text-center">
          Question {qIndex + 1} of {questions.length}
        </p>
        <h2 className="font-serif text-2xl md:text-3xl text-themed text-center leading-snug max-w-xl mx-auto">
          <span className="text-gold mr-3 tabular-nums">{qIndex + 1}.</span>
          {q.prompt}
        </h2>
        <div className="mt-10 space-y-3">
          {q.choices.map((c) => (
            <button
              key={c.id}
              data-testid={`snapshot-choice-${q.id}-${c.id}`}
              onClick={() => onAnswer(q.id, c.id)}
              className={`snapshot-choice ${selected === c.id ? "is-selected" : ""}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-10 flex items-center justify-between">
        <button
          onClick={() => setQIndex((i) => Math.max(0, i - 1))}
          disabled={qIndex === 0}
          className="text-[10px] tracking-[0.3em] uppercase text-themed-muted hover:text-gold disabled:opacity-30 inline-flex items-center gap-1.5"
        >
          <ArrowLeft size={12} /> Back
        </button>
        <p className="text-[10px] tracking-[0.3em] uppercase text-themed/35">
          Tap an answer to continue
        </p>
      </div>
    </div>
  );
};

const ProgressDots = ({ total, current, answers, questions }) => (
  <div className="flex items-center justify-center gap-2">
    {Array.from({ length: total }, (_, i) => {
      const answered = answers[questions[i].id];
      const active = i === current;
      return (
        <span
          key={questions[i].id}
          className={`snapshot-dot ${active ? "is-active" : ""} ${answered ? "is-answered" : ""}`}
        />
      );
    })}
  </div>
);

// ── Email gate ──────────────────────────────────────────────────────
const EmailGate = ({ email, setEmail, name, setName, submitting, onSubmitWithEmail, onSubmitSkip, error }) => (
  <div data-testid="snapshot-email-gate">
    <LivingSectionTitle
      eyebrow="One last beat"
      parts={[
        { text: "Where would you like" },
        { text: "the signal to land?", italic: true },
      ]}
      align="center"
      variant="signal"
      className="text-3xl md:text-4xl text-themed"
    />
    <Reveal delay={120}>
      <p className="mt-6 text-themed-muted text-center text-sm md:text-base leading-relaxed max-w-lg mx-auto">
        Share your email and we will also send you the Morning Frequency Set, a five-step practice that clears scattered effort before the day begins.
      </p>
      <p className="mt-3 text-themed-muted text-center text-[12px]">You can skip this and see your result without sharing.</p>
    </Reveal>
    <div className="mt-8 max-w-md mx-auto space-y-3">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your first name (optional)"
        className="input-bare"
        data-testid="snapshot-name-input"
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email (optional)"
        className="input-bare"
        data-testid="snapshot-email-input"
      />
      {error && <p className="text-rose-400 text-xs">{error}</p>}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
        <button
          onClick={onSubmitWithEmail}
          disabled={submitting || !email}
          className="btn-gold flex-1 inline-flex items-center justify-center gap-2"
          data-testid="snapshot-submit-with-email"
        >
          {submitting ? "Tuning…" : "Receive Snapshot + Morning Set"}
          {!submitting && <ArrowRight size={13} />}
        </button>
        <button
          onClick={onSubmitSkip}
          disabled={submitting}
          className="text-[11px] tracking-[0.3em] uppercase text-themed-muted hover:text-gold py-3"
          data-testid="snapshot-submit-skip"
        >
          {submitting ? "…" : "Skip & see result"}
        </button>
      </div>
    </div>
  </div>
);

// ── Result ──────────────────────────────────────────────────────────
const Result = ({ result, onRetake }) => {
  const rec = RECOMMENDED_LABELS[result.recommended_next] || RECOMMENDED_LABELS.blueprint;
  const { open: openConsultation } = useConsultation();

  const handlePrimaryCTA = useCallback(
    (e) => {
      // For the consultation recommendation we open the modal directly
      // instead of navigating to /#consultation. This (a) keeps the user
      // in their current peak-intent moment, (b) avoids a cross-page
      // hash-scroll race condition where async-loaded homepage content
      // shifted the scroll target onto the wrong section, and (c) lets
      // us pass the snapshot archetype straight into the modal as
      // source_context so Cyrus sees it on the booking.
      if (result.recommended_next === "consultation") {
        e.preventDefault();
        openConsultation({
          category: ARCHETYPE_TO_CATEGORY[result.archetype] || "Not sure yet",
          specific_interest: "Not Sure Yet",
          source_context: `Clarity Snapshot · ${result.title}`,
        });
      }
    },
    [result, openConsultation]
  );

  return (
    <div data-testid="snapshot-result">
      <LivingSectionTitle
        eyebrow="Your Clarity Snapshot"
        parts={[
          { text: "You are" },
          { text: result.title.replace(/^The /, ""), italic: true },
        ]}
        align="center"
        variant="goldline"
        className="text-4xl sm:text-5xl text-themed"
      />
      <Reveal delay={120}>
        <p className="mt-8 text-themed text-center text-lg md:text-xl leading-relaxed font-serif italic max-w-xl mx-auto">
          {result.reflection}
        </p>
      </Reveal>
      <Reveal delay={220}>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="snapshot-result-card">
            <p className="text-[10px] tracking-[0.35em] uppercase text-gold mb-2">What this pattern means</p>
            <p className="text-themed text-sm leading-relaxed">{result.energy_pattern}</p>
          </div>
          <div className="snapshot-result-card">
            <p className="text-[10px] tracking-[0.35em] uppercase text-gold mb-2">A structure that may help</p>
            <p className="text-themed text-sm leading-relaxed">{result.aligned_action}</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={320}>
        <div className="mt-14 text-center">
          <p className="text-[10px] tracking-[0.4em] uppercase text-gold/75 mb-3">Recommended next move</p>
          <p className="font-serif text-xl text-themed max-w-md mx-auto mb-7">{rec.label}</p>
          <div className="flex justify-center">
            <a
              href={rec.href}
              onClick={handlePrimaryCTA}
              className="btn-gold inline-flex items-center gap-2"
              data-testid="snapshot-primary-cta"
            >
              {rec.cta} <ArrowRight size={13} />
            </a>
          </div>
          <p className="mt-6 text-[12px] tracking-[0.14em] uppercase text-gold/70">
            <a
              href="/start-here"
              data-testid="snapshot-secondary-cta"
              className="hover:text-gold transition-colors"
            >
              Not ready for that yet? Compare every entry point on Start Here.
            </a>
          </p>
          <button
            onClick={onRetake}
            className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-themed-muted hover:text-gold transition-colors"
            data-testid="snapshot-retake"
          >
            <RefreshCw size={12} /> Retake the snapshot
          </button>
        </div>
      </Reveal>
    </div>
  );
};

export default FrequencySnapshotPage;
